import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Download, RotateCcw, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sigil } from "@/components/Sigil";
import { Reveal } from "@/components/Reveal";
import { QUESTIONS, PILLAR_RESULTS, ARTWORK_PRODUCT_MATCH, scoreQuiz, wallpaperFilename } from "@/data/pillarJourney";
import { useSEO } from "@/components/Seo";
import { btnGold, btnGhost, eyebrow } from "@/lib/styles";

const EASE = [0.22, 1, 0.36, 1];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function FindYourPillar() {
  useSEO(
    "Find Your Pillar | Threads Unbound™",
    "Three questions. Six pillars. Discover the Threads Unbound artwork that speaks to you today, then make it your wallpaper."
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([null, null, null]);
  const [resultPillar, setResultPillar] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/artworks").then((r) => setArtworks(r.data || [])).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const p = (searchParams.get("pillar") || "").toLowerCase();
    if (PILLAR_RESULTS[p]) setResultPillar(p);
  }, [searchParams]);

  const result = resultPillar ? PILLAR_RESULTS[resultPillar] : null;
  const artwork = result ? artworks.find((a) => a.title === result.artworkTitle) : null;
  const matchedTitle = artwork ? ARTWORK_PRODUCT_MATCH[artwork.title] : null;
  const product = matchedTitle ? products.find((p) => p.title === matchedTitle) : null;

  const choose = (answer) => {
    const next = [...answers];
    next[step] = answer;
    setAnswers(next);
  };

  const reveal = () => setResultPillar(scoreQuiz(answers));

  const retake = () => {
    setAnswers([null, null, null]);
    setStep(0);
    setResultPillar(null);
    navigate("/find-your-pillar", { replace: true });
  };

  const share = async () => {
    const url = `${window.location.origin}/find-your-pillar?pillar=${resultPillar}`;
    const text = `The pillar speaking to me today: ${cap(resultPillar)}. Find yours at Threads Unbound.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Pillar | Threads Unbound™", text, url });
        return;
      } catch (e) {
        /* user dismissed */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch (e) {
      toast.error(url);
    }
  };

  if (result) {
    return (
      <main className="px-6 pb-32 pt-32 md:px-12 md:pt-40 lg:px-20" data-testid="quiz-result">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-6">
              <Sigil pillar={resultPillar} size={64} strokeWidth={2.4} />
              <p className={eyebrow}>The pillar speaking to you today</p>
            </div>
            <h1 className="mt-6 font-serif text-6xl font-light italic text-[#D4AF37] md:text-7xl" data-testid="result-pillar-name">
              {cap(resultPillar)}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70" data-testid="result-explanation">
              {result.explanation}
            </p>
          </Reveal>

          {artwork && (
            <Reveal delay={0.15}>
              <figure className="mt-14 border border-white/10" data-testid="result-artwork">
                <img
                  src={artwork.wallpaper_preview_url || artwork.image_url}
                  alt={artwork.title}
                  className="max-h-[70vh] w-full object-cover object-top"
                />
                <figcaption className="flex items-baseline justify-between gap-4 border-t border-white/10 p-5">
                  <span className="font-serif text-2xl font-light">{artwork.title}</span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">{resultPillar}</span>
                </figcaption>
              </figure>
            </Reveal>
          )}

          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-4">
              {artwork?.wallpaper_url && (
                <a
                  href={`${artwork.wallpaper_url}?download=${wallpaperFilename(artwork.title)}`}
                  download={wallpaperFilename(artwork.title)}
                  data-testid="download-wallpaper-button"
                  className={btnGold}
                >
                  <Download className="h-4 w-4" /> Download My Free Wallpaper
                </a>
              )}
              <Link to={`/gallery?pillar=${resultPillar}`} data-testid="explore-artwork-button" className={btnGhost}>
                Explore This Artwork
              </Link>
              <button onClick={share} data-testid="share-result-button" className={btnGhost}>
                <Share2 className="h-4 w-4" /> Share My Result
              </button>
              <button onClick={retake} data-testid="retake-quiz-button" className={btnGhost}>
                <RotateCcw className="h-4 w-4" /> Retake the Quiz
              </button>
            </div>
            <p className="mt-4 text-xs tracking-wide text-white/30">Wallpaper free for personal use.</p>
          </Reveal>

          <Reveal delay={0.35}>
            {product ? (
              <div className="mt-16 border border-white/10 p-6" data-testid="related-product-card">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">This artwork, wearable</p>
                <div className="mt-5 flex flex-wrap items-center gap-6">
                  <img src={product.image_url} alt={product.title} className="aspect-square w-24 border border-white/10 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-xl font-light">{product.title}</p>
                    <p className="mt-1 font-serif text-lg text-[#D4AF37]">{product.price}</p>
                  </div>
                  <a
                    href={product.etsy_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="related-product-etsy"
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/60 px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
                  >
                    Shop on Etsy <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-16" data-testid="explore-collection-link">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50 transition-colors duration-300 hover:text-[#D4AF37]"
                >
                  Explore the Collection <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </Reveal>
        </div>
      </main>
    );
  }

  const q = QUESTIONS[step];
  const selected = answers[step];
  const isLast = step === QUESTIONS.length - 1;

  return (
    <main className="flex min-h-[100svh] flex-col px-6 pb-24 pt-32 md:px-12 md:pt-40 lg:px-20" data-testid="quiz-page">
      <div className="mx-auto w-full max-w-2xl">
        <Reveal>
          <p className={eyebrow} data-testid="quiz-progress-label">
            Question {step + 1} of {QUESTIONS.length}
          </p>
          <div className="mt-4 h-px w-full bg-white/10">
            <motion.div
              className="h-px bg-[#D4AF37]"
              animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
              transition={{ duration: 0.6, ease: EASE }}
              data-testid="quiz-progress-bar"
            />
          </div>
        </Reveal>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <h1 className="mt-10 font-serif text-3xl font-light leading-snug md:text-4xl" data-testid="quiz-question">
              {q.text}
            </h1>
            <div className="mt-10 space-y-3" role="radiogroup" aria-label="Answer choices">
              {q.answers.map((a, i) => (
                <button
                  key={i}
                  role="radio"
                  aria-checked={selected?.text === a.text}
                  data-testid={`answer-card-${i}`}
                  onClick={() => choose(a)}
                  className={`block min-h-[56px] w-full border p-5 text-left text-base leading-relaxed transition-colors duration-300 ${
                    selected?.text === a.text
                      ? "border-[#D4AF37] bg-[#D4AF37]/5 text-white"
                      : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {a.text}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
            data-testid="quiz-back"
            className="inline-flex min-h-[48px] items-center gap-3 border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-white hover:text-white disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {isLast ? (
            <button onClick={reveal} disabled={!selected} data-testid="quiz-reveal" className={`${btnGold} disabled:opacity-30`}>
              Reveal My Pillar
            </button>
          ) : (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!selected}
              data-testid="quiz-next"
              className={`${btnGhost} disabled:opacity-30`}
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
